// frontend/shared-ui/src/utils/money.js

export const MARGIN_THRESHOLDS = {
    healthy: 30,
    okay: 10,
    thin: 0,
};

export function computeMargin(price, cost) {
    const p = Number(price) || 0;
    const c = Number(cost) || 0;
    if (p <= 0) return null;
    return ((p - c) / p) * 100;
}

export function computeMarkup(price, cost) {
    const p = Number(price) || 0;
    const c = Number(cost) || 0;
    if (c <= 0) return null;
    return ((p - c) / c) * 100;
}

export function computeProfit(price, cost) {
    return (Number(price) || 0) - (Number(cost) || 0);
}

export function marginTier(margin) {
    if (margin === null || margin === undefined || Number.isNaN(margin)) return 'unknown';
    if (margin < MARGIN_THRESHOLDS.thin) return 'loss';
    if (margin < MARGIN_THRESHOLDS.okay) return 'thin';
    if (margin < MARGIN_THRESHOLDS.healthy) return 'okay';
    return 'healthy';
}

/**
 * Format money with smart decimal handling:
 *   - Uses the provided `decimals` if passed.
 *   - Otherwise auto-detects: integers → 0 decimals; any fractional value → 2 decimals.
 *   - Never truncates a fractional value to 0.
 *
 * formatMoney(0.39, { symbol: '$' })        → "$ 0.39"
 * formatMoney(22000, { symbol: 'IQD' })     → "IQD 22,000"
 * formatMoney(22000.5, { symbol: '$' })     → "$ 22,000.50"
 * formatMoney(0.39, { symbol: '$', decimals: 2 })  → "$ 0.39"
 */
export function formatMoney(value, { symbol = 'IQD', decimals } = {}) {
    const n = Number(value) || 0;

    // Auto-detect decimals if caller didn't specify
    let resolvedDecimals;
    if (decimals !== undefined && decimals !== null) {
        resolvedDecimals = Number(decimals);
    } else if (Number.isInteger(n)) {
        resolvedDecimals = 0;
    } else {
        resolvedDecimals = 2;
    }

    const formatted = n.toLocaleString(undefined, {
        minimumFractionDigits: resolvedDecimals,
        maximumFractionDigits: resolvedDecimals,
    });

    return symbol ? `${symbol} ${formatted}` : formatted;
}

/**
 * Format a percentage.
 */
export function formatPercent(value, { decimals = 1, sign = false } = {}) {
    if (value === null || value === undefined || Number.isNaN(value)) return '—';
    const n = Number(value);
    const prefix = sign && n > 0 ? '+' : '';
    return `${prefix}${n.toFixed(decimals)}%`;
}